import React from 'react';
import { Modal, Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { styled } from '@mui/material/styles';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  mode: 'confirm' | 'result' | 'retry';
  message: string;
  title?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

const StModalContainerWithModal = styled(Box)(({ theme }) => ({
  padding: 0,
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  borderRadius: '16px',
  background: theme.palette.white?.main || '#fff',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
  '&:before': {
    content: '""',
    display: 'block',
    background: `linear-gradient(268.67deg, ${
      theme.palette.themeColorSecondary?.main || '#4AA585'
    } -16.55%, ${'#255E5E'} 73.37%)`,
    height: '16px',
    borderRadius: '16px 16px 0px 0px',
  },
  '&:focus': {
    outline: '0 !important',
  },
  [theme.breakpoints.up('lg')]: {
    minWidth: '521px !important',
  },
  [theme.breakpoints.up('sm')]: {
    minWidth: '465px !important',
    maxWidth: '500px !important',
  },
  '@media screen and (max-width:600px)': {
    minWidth: '340px !important',
  },
}));

const StModalContainer = styled(Box)(() => ({
  padding: '12px 24px',
  '@media screen and (max-width:480px)': {
    padding: '24px 18px',
  },
}));

const StFooterBox = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  flexGrow: '1',
  gap: '10px',
  marginTop: '16px',
}));

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  mode,
  message,
  confirmButtonText = 'Continue',
  cancelButtonText = 'Cancel',
}) => {
  return (
    <Modal
      open={open}
      onClose={(_event: Record<string, unknown>, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      aria-labelledby="confirmation-modal-title"
      aria-describedby="confirmation-modal-description"
    >
      <StModalContainerWithModal>
        <StModalContainer>
          <Typography
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: '#000',
            }}
          >
            {mode === 'confirm' || mode === 'retry' ? (
              message
            ) : (
              <RichText field={{ value: message }} />
            )}
            <IconButton
              style={{ top: '-10px' }}
              color="inherit"
              aria-label="close modal button"
              edge="end"
              onClick={onClose}
              data-testid="close-confirmation-dialog"
            >
              <CloseIcon sx={{ color: '#666' }} />
            </IconButton>
          </Typography>

          <StFooterBox>
            {(mode === 'confirm' || mode === 'retry') && (
              <Button
                size="small"
                onClick={onCancel}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: '30px',
                  padding: '10px 10px',
                  fontSize: '14px',
                  lineHeight: '16px',
                  color: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                  '&:hover': {
                    borderColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                    opacity: 0.9,
                  },
                }}
              >
                {cancelButtonText}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={onConfirm}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '30px',
                padding: '10px 10px',
                fontSize: '14px',
                lineHeight: '16px',
                backgroundColor: (theme: any) => theme.palette.themeColor?.main || '#255E5E',
                color: '#ffffff',
                '&:hover': {
                  background: 'rgb(255, 255, 255)',
                  color: (theme: any) => theme.palette.themeColor?.main || 'rgb(37, 94, 94)',
                  borderColor: (theme: any) => theme.palette.themeColor?.main || 'rgb(37, 94, 94)',
                },
              }}
            >
              {mode === 'confirm'
                ? confirmButtonText
                : mode === 'retry'
                  ? confirmButtonText
                  : 'Done'}
            </Button>
          </StFooterBox>
        </StModalContainer>
      </StModalContainerWithModal>
    </Modal>
  );
};

export default ConfirmationDialog;
